/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { Grid, Link, Box, Tooltip, IconButton } from '@material-ui/core';
import { Refresh } from '@material-ui/icons';
import MUIDataTable from 'mui-datatables';
import moment from 'moment';
import { useLazyQuery, useQuery } from '@apollo/client';
import { useMultipleLoading } from 'hooks/useMultipleLoading.hook';
import MUITableLoading from 'components/MUITableLoading/MUITableLoading';
import Layout from 'components/Layout/Layout';
import DrawerContents from '../../../components/DrawerContents';
import PageTitle from '../../../components/PageTitle/PageTitle';
import WriterReportInfo from '../components/WriterReportInfo';
import PageError from '../../../components/PageError';
import { DataRangeFilter } from '../../../components/DataRangeFilter/DataRangeFilter';
import { GET_WRITERS_REPORT } from '../../../queries/reports';
import { GET_REVIEWERS_WRITERS_TRANSLATORS } from '../../../queries/user';
import { Toast } from '../../../components/Toast';
import Loading from '../../../components/Loading';
import { UserService } from '../../../services/User/UserService';

export default function WritersReport() {
  const sites = UserService.useSitesFromAccess();

  const [selectedWriterId, setSelectedWriterId] = useState('');
  const [showDrawer, setShowDrawer] = useState('');
  const [writers, setWriters] = useState([]);
  const [reports, setReports] = useState([]);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [selectedStartDate, setSelectedStartDate] = useState(
    moment().subtract(6, 'days').startOf('day').toDate(),
  );
  const [selectedEndDate, setSelectedEndDate] = useState(
    moment().endOf('day').toDate(),
  );
  const [openLoading, setOpenLoading] = useState(false);
  const [dateTypeFilter, setDateTypeFilter] = useState('Last7Days');
  const [total, setTotal] = useState(10);

  const [loaded, setCompleted] = useMultipleLoading('users', 'reports');

  const queryUsers = useQuery(GET_REVIEWERS_WRITERS_TRANSLATORS, {
    onCompleted: () => setCompleted('users'),
  });

  const [getReports, queryReports] = useLazyQuery(GET_WRITERS_REPORT, {
    variables: {
      writers: writers.map((writer) => writer.id),
      dateStart: selectedStartDate,
      dateEnd: selectedEndDate,
      sites,
      status: null,
    },
    fetchPolicy: 'network-only',
    onCompleted: () => setCompleted('reports'),
  });
  const { error } = queryReports;

  useEffect(() => {
    if (queryUsers.data) {
      const {
        data: { userListByRole },
      } = queryUsers;

      setWriters(userListByRole);
      getReports();
    }
  }, [queryUsers]);

  const updateReportsTable = (page, rowsPerPage) => {
    setPage(page);
    setLimit(rowsPerPage);
  };

  useEffect(() => {
    if (queryReports.data) {
      const {
        data: { titlesWritersReport },
      } = queryReports;

      const reports = titlesWritersReport.reports.map((titlesWriterReport) => {
        const foundWriterById = writers.find(
          (writer) => writer.id === titlesWriterReport.writerId,
        );

        return {
          ...titlesWriterReport,
          writerName: foundWriterById?.name || '',
        };
      });

      setReports(reports);
      setTotal(titlesWritersReport.total);
    }
  }, [queryReports, writers]);

  const handleRowClick = (writerId) => {
    setSelectedWriterId(writerId);
    setShowDrawer(true);
  };

  const handleCloseDrawer = () => {
    setShowDrawer(false);
  };

  const refetchReports = async (showBackdrop = true) => {
    if (showBackdrop) setOpenLoading(true);
    queryReports.refetch();

    if (showBackdrop) {
      setOpenLoading(false);
      Toast('Writers Report updated successfully!');
    }
  };

  const renderRefreshList = () => (
    <Tooltip title="Refresh contents">
      <IconButton aria-label="refresh" onClick={() => refetchReports()}>
        <Refresh />
      </IconButton>
    </Tooltip>
  );

  const WritersRow = (writerName) => {
    return (
      // eslint-disable-next-line jsx-a11y/anchor-is-valid
      <Link>{writerName}</Link>
    );
  };

  const columns = [
    {
      name: 'writerId',
      label: 'WRITER ID',
      options: {
        display: false,
        filter: false,
      },
    },
    {
      name: 'writerName',
      label: 'WRITER',
      options: {
        filter: false,
        filterOptions: {
          names: writers.map((writer) => writer.name),
        },
        customBodyRender: (value) => {
          if (value) {
            return WritersRow(value);
          }
          return '';
        },
      },
    },
    {
      name: 'article',
      label: 'ARTICLE',
      options: {
        display: true,
      },
    },
    {
      name: 'news',
      label: 'NEWS',
      options: {
        display: true,
      },
    },
    {
      name: 'translator',
      label: 'TRANSLATOR',
      options: {
        display: true,
      },
    },
    {
      name: 'others',
      label: 'OTHER TAGS',
      options: {
        display: true,
      },
    },
  ];

  const FilterDialogFooter = () => <Box width={360} />;

  const options = {
    selectableRowsOnClick: false,
    selectableRowsHeader: false,
    selectableRows: 'none',
    responsive: 'standard',
    page,
    count: total,
    rowsPerPage: limit,
    customFilterDialogFooter: () => <FilterDialogFooter />,
    onRowClick: (rowData) => handleRowClick(rowData[0]),
    searchPlaceholder: 'Search for a writer...',
    onTableChange: (action, tableState) => {
      switch (action) {
        case 'changeRowsPerPage':
          updateReportsTable(tableState.page, tableState.rowsPerPage);
          break;
        case 'changePage':
          updateReportsTable(tableState.page, tableState.rowsPerPage);
          break;
        case 'filterChange':
          updateReportsTable(tableState.page, tableState.rowsPerPage);
          break;
        default:
          break;
      }
    },
  };

  return (
    <Layout>
      <Loading openLoading={openLoading} />
      <DrawerContents
        title="Writer info"
        statusDrawer={showDrawer}
        setStatusDrawer={setShowDrawer}
        onCloseDrawer={handleCloseDrawer}
      >
        <WriterReportInfo
          writerId={selectedWriterId}
          sites={sites}
          actualDateTypeFilter={dateTypeFilter}
          actualSelectedStartDate={selectedStartDate}
          actualSelectedEndDate={selectedEndDate}
        />
      </DrawerContents>
      <Grid container spacing={0}>
        <Grid item xs={4}>
          <PageTitle title="Writers Report" />
        </Grid>
        <Grid item xs={8}>
          <DataRangeFilter
            selectedStartDate={selectedStartDate}
            setSelectedStartDate={setSelectedStartDate}
            selectedEndDate={selectedEndDate}
            setSelectedEndDate={setSelectedEndDate}
            setDateTypeFilter={setDateTypeFilter}
            dateTypeFilter={dateTypeFilter}
            writers={writers}
            setPage={setPage}
          />
        </Grid>
      </Grid>
      {!loaded() ? (
        <MUITableLoading
          withRefresh
          selectable="none"
          columns={['WRITER', 'ARTICLE', 'NEWS', 'TRANSLATOR', 'OTHER TAGS']}
        />
      ) : error ? (
        <PageError />
      ) : (
        <MUIDataTable
          title={renderRefreshList()}
          data={reports}
          columns={columns}
          options={options}
        />
      )}
    </Layout>
  );
}
